// DigitalNext — leermodel (datalaag voor de schermen).
// Fase 2: gevoed vanuit deze typed config (4 thema's + modules + zelfscan + toets).
// In fase 3/4 wordt de bron hiervan vervangen door de backend-API; de schermen
// blijven dan gelijk omdat ze tegen deze types praten.

import aiSa from './sa/ai';
import dgSa from './sa/dg';
import mwSa from './sa/mw';
import dvSa from './sa/dv';
import smSa from './sa/sm';
import { SaItem, SaSet } from './sa/types';

export type ThemeId = 'ai' | 'dg' | 'mw' | 'dv' | 'sm';

export interface Theme {
  id: ThemeId;
  name: string;
  desc: string;       // korte omschrijving (1 zin)
  long: string;       // langere omschrijving
  accent: string;     // hex accentkleur
  tint: string;       // hex zachte tint
  textc: string;      // hex tekstkleur op tint
  mods: number;       // aantal modules
}

export interface ModuleItem {
  title: string;
  sub: string;        // bv. "3 onderdelen"
  dur: string;        // bv. "15 min"
}

export interface ToetsVraag {
  q: string;
  opts: string[];
  correct: number;    // index van juiste optie
}

// Vaste rollen: zelfscan = goud, toets = blauw (royal).
export const GOLD = '#C29A33';
export const ROYAL = '#2440CF';

export const THEMES: Theme[] = [
  {
    id: 'ai', name: 'AI-geletterdheid',
    desc: 'Begrijp en benut AI met vertrouwen in je werk.',
    long: 'Van begrijpen wat AI is tot zelf effectief werken met AI-tools en prompts. Je leert kansen én risico’s herkennen en AI verantwoord inzetten.',
    accent: '#2440CF', tint: '#EAEEFC', textc: '#2440CF', mods: 8,
  },
  {
    id: 'dg', name: 'Digitale geletterdheid',
    desc: 'De digitale basisvaardigheden helemaal op orde.',
    long: 'De basis van veilig en handig werken met apparaten, bestanden, samenwerken en online tools — het fundament onder alle andere thema’s.',
    accent: '#C29A33', tint: '#F6EFD9', textc: '#8A6D12', mods: 6,
  },
  {
    id: 'mw', name: 'Mediawijsheid',
    desc: 'Kritisch omgaan met media, bronnen en informatie.',
    long: 'Herken nepnieuws, beoordeel bronnen en begrijp hoe media en algoritmes je beeld beïnvloeden — zodat je bewuste keuzes maakt.',
    accent: '#8A2C3D', tint: '#F6E7EA', textc: '#8A2C3D', mods: 7,
  },
  {
    id: 'dv', name: 'Digitale veiligheid',
    desc: 'Veilig en bewust online — thuis en op het werk.',
    long: 'Wachtwoorden, phishing, privacy en veilig delen. Praktische vaardigheden om jezelf en je organisatie online te beschermen.',
    accent: '#2E2A25', tint: '#EEEAE3', textc: '#57514A', mods: 5,
  },
  {
    id: 'sm', name: 'Social media',
    desc: 'Grip op de platforms waar we leven, werken en praten.',
    long: 'Van de eerste fora tot TikTok, Discord en de feed van vandaag. Je leert welk platform wat doet en voor wie, waar de kracht zit en waar het knelt, en hoe je bewust de regie houdt over je eigen gebruik.',
    accent: '#0E7C86', tint: '#E1F0F1', textc: '#0A5A62', mods: 9,
  },
];

export const themeById = (id: ThemeId) => THEMES.find((t) => t.id === id) || THEMES[0];

export const MODULES: Record<ThemeId, ModuleItem[]> = {
  ai: [
    { title: 'Wat is AI?', sub: '3 onderdelen', dur: '15 min' },
    { title: 'AI in het dagelijks werk', sub: '4 onderdelen', dur: '20 min' },
    { title: 'Prompten in de praktijk', sub: '4 onderdelen', dur: '25 min' },
    { title: 'Betrouwbaarheid & bronnen', sub: '3 onderdelen', dur: '18 min' },
    { title: 'AI verantwoord gebruiken', sub: '4 onderdelen', dur: '22 min' },
    { title: 'Privacy & data', sub: '3 onderdelen', dur: '16 min' },
    { title: 'AI-tools verkennen', sub: '5 onderdelen', dur: '30 min' },
    { title: 'Aan de slag: jouw casus', sub: '2 onderdelen', dur: '20 min' },
  ],
  dg: [
    { title: 'Apparaten & bestanden', sub: '3 onderdelen', dur: '15 min' },
    { title: 'Online samenwerken', sub: '4 onderdelen', dur: '20 min' },
    { title: 'Slim zoeken', sub: '3 onderdelen', dur: '14 min' },
    { title: 'Communiceren & delen', sub: '3 onderdelen', dur: '16 min' },
    { title: 'Digitale administratie', sub: '4 onderdelen', dur: '22 min' },
    { title: 'Handig automatiseren', sub: '3 onderdelen', dur: '18 min' },
  ],
  mw: [
    { title: 'Wat is mediawijsheid?', sub: '3 onderdelen', dur: '15 min' },
    { title: 'Bronnen beoordelen', sub: '4 onderdelen', dur: '20 min' },
    { title: 'Nepnieuws herkennen', sub: '4 onderdelen', dur: '22 min' },
    { title: 'Algoritmes & filterbubbels', sub: '3 onderdelen', dur: '18 min' },
    { title: 'Beeld en manipulatie', sub: '3 onderdelen', dur: '16 min' },
    { title: 'Jouw digitale voetafdruk', sub: '3 onderdelen', dur: '15 min' },
    { title: 'Bewust delen', sub: '2 onderdelen', dur: '12 min' },
  ],
  dv: [
    { title: 'Veilige wachtwoorden', sub: '3 onderdelen', dur: '14 min' },
    { title: 'Phishing herkennen', sub: '4 onderdelen', dur: '20 min' },
    { title: 'Privacy-instellingen', sub: '3 onderdelen', dur: '16 min' },
    { title: 'Veilig delen & opslaan', sub: '3 onderdelen', dur: '15 min' },
    { title: 'Wat te doen bij een incident', sub: '2 onderdelen', dur: '12 min' },
  ],
  sm: [
    { title: 'Wat is social media eigenlijk?', sub: '3 onderdelen', dur: '15 min' },
    { title: 'Van fora tot feed: een korte geschiedenis', sub: '3 onderdelen', dur: '16 min' },
    { title: 'Het platformlandschap van nu', sub: '4 onderdelen', dur: '24 min' },
    { title: 'Social media via gaming', sub: '3 onderdelen', dur: '15 min' },
    { title: 'Waar social media écht goed voor is', sub: '3 onderdelen', dur: '16 min' },
    { title: 'De schaduwkant: algoritme, vergelijken, desinformatie', sub: '4 onderdelen', dur: '22 min' },
    { title: 'Grip houden en minderen', sub: '3 onderdelen', dur: '18 min' },
    { title: 'Social media in de samenleving', sub: '3 onderdelen', dur: '16 min' },
    { title: 'Identiteit, het gesprek en de toekomst', sub: '3 onderdelen', dur: '17 min' },
  ],
};

export const SA_SCALE = ['Helemaal oneens', 'Oneens', 'Neutraal', 'Eens', 'Helemaal eens'];

// Zelfscan: 10 stellingen per thema (luchtig, herkenbaar).
export const SA_QUESTIONS: Record<ThemeId, string[]> = {
  ai: [
    'Ik begrijp in grote lijnen wat AI is en hoe het werkt.',
    'Ik kan een duidelijke opdracht (prompt) geven aan een AI-tool.',
    'Ik merk het wanneer AI-antwoorden onbetrouwbaar of onjuist kunnen zijn.',
    'Ik weet hoe ik AI veilig en verantwoord gebruik op mijn werk.',
    'Ik gebruik AI al regelmatig in mijn dagelijkse werk.',
    'Ik weet voor welke klusjes AI me wél helpt en voor welke niet.',
    'Ik kan mijn vraag aanscherpen als het antwoord nog niet klopt.',
    'Ik controleer belangrijke uitkomsten van AI altijd zelf na.',
    'Ik snap dat AI leert van voorbeelden en daardoor scheef kan zitten.',
    'Ik kan in gewone woorden aan een collega uitleggen wat AI doet.',
  ],
  dg: [
    'Ik vind en orden bestanden zonder moeite op mijn apparaten.',
    'Ik werk vlot online samen in documenten en mappen.',
    'Ik weet hoe ik gericht en slim zoek naar informatie.',
    'Ik kan veilig bestanden delen met collega’s of klanten.',
    'Ik pak nieuwe apps of tools snel op als dat nodig is.',
    'Ik weet het verschil tussen iets lokaal en in de cloud bewaren.',
    'Ik los kleine computerstoringen meestal zelf op.',
    'Ik kan een document omzetten naar een ander bestandstype als dat nodig is.',
    'Ik houd mijn apparaten en apps netjes up-to-date.',
    'Ik weet hoe ik een oudere versie van een bestand terughaal.',
  ],
  mw: [
    'Ik check of een bron betrouwbaar is voordat ik iets geloof.',
    'Ik herken signalen van nepnieuws of misleiding.',
    'Ik begrijp hoe algoritmes bepalen wat ik online zie.',
    'Ik weet dat beeld en video overtuigend nep kunnen zijn.',
    'Ik denk na voor ik iets deel of doorstuur.',
    'Ik merk wanneer een bericht vooral op mijn emotie speelt.',
    'Ik kan reclame of een betaalde boodschap onderscheiden van gewone inhoud.',
    'Ik zoek bij een opvallend bericht ook even naar een andere bron.',
    'Ik let op hoe ik zelf overkom in wat ik online plaats.',
    'Ik weet hoe ik kan reageren als ik iets onjuists voorbij zie komen.',
  ],
  dv: [
    'Ik gebruik sterke, verschillende wachtwoorden.',
    'Ik herken een phishingmail of nep-bericht meestal.',
    'Ik weet welke privacy-instellingen belangrijk zijn.',
    'Ik deel en bewaar gevoelige gegevens op een veilige manier.',
    'Ik weet wat ik moet doen als er iets misgaat (bv. een hack).',
    'Ik heb een extra controle (zoals 2FA) aanstaan op belangrijke accounts.',
    'Ik denk na voordat ik koppel met openbare wifi.',
    'Ik maak regelmatig een back-up van mijn belangrijke bestanden.',
    'Ik weet welke gegevens van mij online te vinden zijn.',
    'Ik vertrouw onverwachte links of bijlagen niet zomaar.',
  ],
  sm: [
    'Ik weet welke socialmediaplatforms er zijn en waar ze voor bedoeld zijn.',
    'Ik kies bewust welke platforms ik gebruik en waarvoor.',
    'Ik begrijp hoe een algoritme bepaalt wat ik in mijn feed zie.',
    'Ik merk het als een platform me langer vasthoudt dan ik wil.',
    'Ik denk na over wat ik deel en wie het kan zien.',
    'Ik herken signalen van te veel of ongezond gebruik bij mezelf.',
    'Ik kan mijn schermtijd op social media bewust naar beneden brengen.',
    'Ik weet hoe organisaties zoals de overheid en banken social media inzetten.',
    'Ik kan een gesprek voeren over social media met bijvoorbeeld kinderen of familie.',
    'Ik denk na over wat mijn gebruik zegt over mezelf en mijn identiteit.',
  ],
};

// Toets: 10 meerkeuzevragen per thema (toegankelijk, niet te technisch).
export const TOETS_QUESTIONS: Record<ThemeId, ToetsVraag[]> = {
  ai: [
    { q: 'Wat is een „prompt” bij een AI-tool?', opts: [
      'De opdracht of vraag die je aan de AI geeft',
      'Het automatisch opslaan van je werk',
      'Een foutmelding van het systeem',
      'De snelheid van de computer'], correct: 0 },
    { q: 'Welke uitspraak over AI-antwoorden klopt het best?', opts: [
      'AI heeft altijd gelijk',
      'AI kan overtuigend klinken maar tóch fouten maken',
      'AI gebruikt alleen gecontroleerde bronnen',
      'AI begrijpt precies wat je bedoelt'], correct: 1 },
    { q: 'Wat doe je het best bij twijfel over informatie uit een AI-tool?', opts: [
      'Je neemt het meteen over',
      'Je negeert het helemaal',
      'Je controleert het bij een betrouwbare bron',
      'Je stelt dezelfde vraag nog een keer'], correct: 2 },
    { q: 'Welke gegevens zet je beter níét in een openbare AI-tool?', opts: [
      'Een algemene vraag over een onderwerp',
      'Vertrouwelijke of persoonsgegevens',
      'Een openbaar nieuwsbericht',
      'Een verzonnen voorbeeld'], correct: 1 },
    { q: 'Wat betekent het als AI „hallucineert”?', opts: [
      'De tool wordt langzamer',
      'De tool verzint informatie die niet klopt',
      'De tool stopt ermee',
      'De tool herhaalt je vraag'], correct: 1 },
    { q: 'Hoe krijg je vaak een beter antwoord van een AI-tool?', opts: [
      'Door zo kort mogelijk te vragen',
      'Door duidelijk te zeggen wat je wilt en wat de bedoeling is',
      'Door dezelfde vraag steeds te herhalen',
      'Door alles in hoofdletters te typen'], correct: 1 },
    { q: 'Waar leert een AI-tool zijn antwoorden eigenlijk van?', opts: [
      'Van heel veel voorbeelden en teksten',
      'Van een vaste lijst met goedgekeurde feiten',
      'Van jouw computer alleen',
      'Van niets, het verzint alles ter plekke'], correct: 0 },
    { q: 'Waarom kan een AI-tool soms bevooroordeeld reageren?', opts: [
      'Omdat de tool een eigen mening heeft',
      'Omdat de voorbeelden waarvan hij leerde al scheef konden zijn',
      'Omdat hij expres fouten maakt',
      'Omdat hij te snel werkt'], correct: 1 },
    { q: 'Wie blijft verantwoordelijk voor werk dat je met AI maakt?', opts: [
      'De maker van de AI-tool',
      'Niemand',
      'Jij zelf',
      'De computer'], correct: 2 },
    { q: 'Voor welke taak is een AI-tool meestal het handigst?', opts: [
      'Een eerste opzet of samenvatting maken',
      'Beslissen wie je moet aannemen',
      'Een definitief medisch oordeel geven',
      'Garanderen dat iets juridisch klopt'], correct: 0 },
  ],
  dg: [
    { q: 'Wat is een handige manier om bestanden terug te vinden?', opts: [
      'Alles op het bureaublad zetten',
      'Een logische mappenstructuur en duidelijke namen',
      'Alleen onthouden waar het staat',
      'Alles „untitled” noemen'], correct: 1 },
    { q: 'Wat is de winst van online samenwerken in één document?', opts: [
      'Iedereen werkt in dezelfde, actuele versie',
      'Je hebt geen internet meer nodig',
      'Niemand kan meer iets wijzigen',
      'Het document wordt automatisch korter'], correct: 0 },
    { q: 'Hoe zoek je gerichter op internet?', opts: [
      'Door zo veel mogelijk woorden los te typen',
      'Door specifieke zoektermen en filters te gebruiken',
      'Door altijd de eerste link te nemen',
      'Door alleen in hoofdletters te typen'], correct: 1 },
    { q: 'Wat is veilig om een groot bestand te delen?', opts: [
      'Een gedeelde link met de juiste rechten',
      'Het op een openbare site zetten',
      'Het wachtwoord in dezelfde mail meesturen',
      'Het aan iedereen doorsturen'], correct: 0 },
    { q: 'Wat helpt bij terugkerend, saai computerwerk?', opts: [
      'Het elke keer handmatig opnieuw doen',
      'Een sjabloon of eenvoudige automatisering',
      'Het maar overslaan',
      'Een snellere muis kopen'], correct: 1 },
    { q: 'Wat is het voordeel van iets in de cloud bewaren?', opts: [
      'Je komt er alleen op één vaste computer bij',
      'Het verdwijnt automatisch na een week',
      'Je komt er op meerdere apparaten bij en het staat veilig opgeslagen',
      'Het neemt ruimte in op je telefoon'], correct: 2 },
    { q: 'Waarom is het slim om updates te installeren?', opts: [
      'Ze lossen fouten op en dichten beveiligingslekken',
      'Ze maken je apparaat altijd sneller',
      'Ze zijn verplicht maar verder nutteloos',
      'Ze verwijderen je bestanden'], correct: 0 },
    { q: 'Je verstuurt per ongeluk een verkeerde versie van een document. Wat helpt vaak?', opts: [
      'Helemaal opnieuw beginnen',
      'De versiegeschiedenis gebruiken om een eerdere versie terug te halen',
      'De computer opnieuw opstarten',
      'Wachten tot het vanzelf goed komt'], correct: 1 },
    { q: 'Wat betekent een „pdf” handig voor het delen van een document?', opts: [
      'Het kan door iedereen vrij worden bewerkt',
      'De opmaak blijft hetzelfde op elk apparaat',
      'Het werkt alleen op jouw computer',
      'Het maakt het bestand altijd kleiner dan een foto'], correct: 1 },
    { q: 'Wat doe je het best als een programma vastloopt?', opts: [
      'Meteen een nieuwe laptop kopen',
      'Hard op het scherm tikken',
      'Het programma sluiten en opnieuw openen, of het apparaat herstarten',
      'Alles wissen en opnieuw installeren'], correct: 2 },
  ],
  mw: [
    { q: 'Waaraan herken je een betrouwbare bron meestal?', opts: [
      'Veel hoofdletters en uitroeptekens',
      'Een bekende, controleerbare afzender met bronvermelding',
      'Het staat hoog in de zoekresultaten',
      'Het bevestigt wat je al dacht'], correct: 1 },
    { q: 'Wat is een veelvoorkomend kenmerk van nepnieuws?', opts: [
      'Een rustige, genuanceerde toon',
      'Sterke emotie en geen of vage bronnen',
      'Een datum en een auteur',
      'Een correctie onderaan'], correct: 1 },
    { q: 'Wat is een „filterbubbel”?', opts: [
      'Een schoonmaakfunctie van je browser',
      'Dat je vooral te zien krijgt wat past bij je eerdere gedrag',
      'Een soort virus',
      'Een betaald abonnement'], correct: 1 },
    { q: 'Een foto ziet er echt uit, maar klopt iets niet. Wat doe je?', opts: [
      'Direct delen, ziet er echt uit',
      'De herkomst en context checken voordat je iets gelooft',
      'Aannemen dat het nep is en negeren',
      'Alleen op de reacties afgaan'], correct: 1 },
    { q: 'Wat is verstandig vóór je iets deelt?', opts: [
      'Checken of het klopt en of delen nodig is',
      'Zo snel mogelijk als eerste delen',
      'Alleen de kop lezen',
      'Het altijd anoniem doen'], correct: 0 },
    { q: 'Een bericht maakt je meteen heel boos of bang. Wat is dat vaak een teken van?', opts: [
      'Dat het zeker waar is',
      'Dat het misschien bedoeld is om je te raken en te laten delen',
      'Dat de afzender betrouwbaar is',
      'Dat je het direct moet doorsturen'], correct: 1 },
    { q: 'Hoe herken je vaak reclame tussen gewone berichten?', opts: [
      'Het heeft nooit plaatjes',
      'Er staat een aanduiding bij zoals „advertentie” of „gesponsord”',
      'Het is altijd grappig bedoeld',
      'Het staat altijd onderaan de pagina'], correct: 1 },
    { q: 'Wat is een goede manier om een opvallend nieuwsbericht te checken?', opts: [
      'Kijken of meer betrouwbare bronnen hetzelfde melden',
      'Alleen de reacties eronder lezen',
      'Aannemen dat het klopt door het mooie plaatje',
      'Het meteen doorsturen om te vragen of het klopt'], correct: 0 },
    { q: 'Waar kun je op letten bij wat je zelf online plaatst?', opts: [
      'Dat het zo veel mogelijk likes oplevert',
      'Dat het anoniem is, dan maakt het niet uit',
      'Dat je later nog achter je woorden en beeld kunt staan',
      'Dat je het zo snel mogelijk plaatst'], correct: 2 },
    { q: 'Je ziet duidelijk onjuiste informatie langskomen. Wat is een nette reactie?', opts: [
      'Het zelf ook delen met een grapje erbij',
      'De persoon publiek belachelijk maken',
      'Rustig een betrouwbare bron of correctie aandragen',
      'Niets, en het juist verder verspreiden'], correct: 2 },
  ],
  dv: [
    { q: 'Wat is een sterk wachtwoord?', opts: [
      'Je naam met een 1 erachter',
      'Lang en uniek, het liefst via een wachtwoordmanager',
      'Hetzelfde wachtwoord overal',
      '„wachtwoord123”'], correct: 1 },
    { q: 'Wat is een typisch teken van phishing?', opts: [
      'Een persoonlijke mail van een bekende collega',
      'Urgentie + een vreemde link of bijlage',
      'Een nieuwsbrief waarvoor je je aanmeldde',
      'Een afspraakbevestiging die je verwachtte'], correct: 1 },
    { q: 'Wat doet tweestapsverificatie (2FA)?', opts: [
      'Het maakt je wachtwoord langer',
      'Het vraagt naast je wachtwoord om een extra bevestiging',
      'Het onthoudt je wachtwoord',
      'Het verwijdert oude accounts'], correct: 1 },
    { q: 'Hoe deel je gevoelige gegevens het veiligst?', opts: [
      'Via een beveiligde, gerichte manier met de juiste persoon',
      'In een openbare groepschat',
      'Door het op social media te zetten',
      'Door het aan iedereen te mailen'], correct: 0 },
    { q: 'Je denkt dat je gehackt bent. Wat is een goede eerste stap?', opts: [
      'Niets doen en afwachten',
      'Wachtwoorden wijzigen en het melden',
      'Je account verwijderen',
      'De computer weggooien'], correct: 1 },
    { q: 'Waarom is openbare wifi (bv. in een café) extra oppassen?', opts: [
      'Het is altijd te traag om iets te doen',
      'Anderen op dat netwerk kunnen soms meekijken met wat je verstuurt',
      'Het kost altijd geld',
      'Je telefoon gaat ervan stuk'], correct: 1 },
    { q: 'Waarom is een back-up zo handig?', opts: [
      'Je apparaat wordt er sneller van',
      'Je hebt geen wachtwoord meer nodig',
      'Je raakt je bestanden niet kwijt bij diefstal, schade of een hack',
      'Het maakt je bestanden mooier'], correct: 2 },
    { q: 'Wat doe je het best met een onverwachte bijlage in je mail?', opts: [
      'Eerst checken of je hem verwacht en of de afzender klopt',
      'Altijd meteen openen',
      'Doorsturen naar collega’s om te vragen',
      'Je computer uitzetten'], correct: 0 },
    { q: 'Wat is een verstandige omgang met je privacy op websites?', opts: [
      'Overal zo veel mogelijk gegevens invullen',
      'Bewust kiezen welke gegevens je deelt en cookies beperken',
      'Altijd alles accepteren zonder te kijken',
      'Je echte adres bij elk spelletje invullen'], correct: 1 },
    { q: 'Je krijgt een telefoontje van “de helpdesk” die je wachtwoord vraagt. Wat doe je?', opts: [
      'Je geeft het wachtwoord, ze zijn van de helpdesk',
      'Je geeft het niet — een echte helpdesk vraagt nooit je wachtwoord',
      'Je leest het langzaam voor zodat ze het goed horen',
      'Je verandert het wachtwoord in iets makkelijks en geeft dat'], correct: 1 },
  ],
  sm: [
    { q: 'Wat maakt “social media” anders dan een gewone nieuwssite of tv-zender?', opts: [
      'Je kunt er zelf inhoud plaatsen en op anderen reageren',
      'Het is altijd gratis',
      'Er staat nooit reclame op',
      'Het werkt alleen op een telefoon'], correct: 0 },
    { q: 'Welk platform is van oudsher vooral gericht op zakelijk netwerken en werk?', opts: [
      'Snapchat',
      'TikTok',
      'LinkedIn',
      'Discord'], correct: 2 },
    { q: 'Hyves, ICQ en MSN hebben met elkaar gemeen dat ze…', opts: [
      'nooit populair zijn geweest in Nederland',
      'vroege vormen van online contact waren die nu grotendeels verdwenen zijn',
      'pas na 2020 zijn ontstaan',
      'allemaal nog marktleider zijn'], correct: 1 },
    { q: 'Waarom zit een deel van de jongeren vooral op platforms als Discord en in games?', opts: [
      'Daar is het verboden voor volwassenen',
      'Het zijn plekken waar je samen speelt én met elkaar praat',
      'Die platforms hebben geen chatfunctie',
      'Het kost daar altijd geld'], correct: 1 },
    { q: 'Wat bepaalt vooral wat je in je feed te zien krijgt?', opts: [
      'De volgorde waarin berichten zijn geplaatst',
      'Een algoritme dat inschat wat jou lang bezig houdt',
      'Een redactie die elk bericht handmatig kiest',
      'Het toeval'], correct: 1 },
    { q: 'Wat is een veelvoorkomend signaal van ongezond socialmediagebruik?', opts: [
      'Je gebruikt maar één app',
      'Je grijpt vaak ongemerkt naar je telefoon en voelt onrust als dat niet kan',
      'Je hebt je meldingen uitgezet',
      'Je plaatst zelf weinig'], correct: 1 },
    { q: 'Wat is een realistische manier om “social-media-minderen” aan te pakken?', opts: [
      'In één keer alle accounts verwijderen en nooit meer kijken',
      'Kleine drempels inbouwen, zoals meldingen uit en apps van je beginscherm halen',
      'Een nieuwere, snellere telefoon kopen',
      'Alleen ’s nachts kijken'], correct: 1 },
    { q: 'Hoe zet de zorg of een bank social media meestal in?', opts: [
      'Alleen om advertenties te verkopen',
      'Voor voorlichting, vragen beantwoorden en mensen sneller bereiken',
      'Het mag daar wettelijk niet',
      'Uitsluitend voor interne communicatie'], correct: 1 },
    { q: 'Iemand laat online vooral hoogtepunten zien. Wat is verstandig om te bedenken?', opts: [
      'Dat hun leven echt zo perfect is als het lijkt',
      'Dat een feed een geredigeerde etalage is, niet het hele verhaal',
      'Dat je net zo veel moet plaatsen',
      'Dat ze het vast nep bedoelen'], correct: 1 },
    { q: 'Worden AI-chatbots een soort social media? Wat is de beste kijk daarop?', opts: [
      'Nee, een AI heeft niets met contact of platforms te maken',
      'Deels: ze vragen je aandacht en gesprek op een vergelijkbare manier, dus dezelfde alertheid helpt',
      'Ja, het is precies hetzelfde als Facebook',
      'Dat kan niet, AI bestaat nog niet'], correct: 1 },
  ],
};

// ---- Voortgang & scores (anoniem, in localStorage; in fase 3 naar backend) ----
export interface ThemeState { sa: number | null; toets: number | null; doneModules: number; }
type Store = Record<ThemeId, ThemeState>;

const KEY = 'dn_progress_v2';
const DEFAULT_STORE: Store = {
  ai: { sa: null, toets: null, doneModules: 0 },
  dg: { sa: null, toets: null, doneModules: 0 },
  mw: { sa: null, toets: null, doneModules: 0 },
  dv: { sa: null, toets: null, doneModules: 0 },
  sm: { sa: null, toets: null, doneModules: 0 },
};

export function loadProgress(): Store {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { ...DEFAULT_STORE, ...JSON.parse(raw) };
  } catch (_) { /* negeer */ }
  return { ...DEFAULT_STORE };
}

// Optionele write-through naar de backend (gezet door de AuthProvider bij login).
let backendSync: ((id: ThemeId, st: ThemeState) => void) | null = null;
export function setBackendSync(fn: ((id: ThemeId, st: ThemeState) => void) | null) { backendSync = fn; }

export function saveThemeState(id: ThemeId, patch: Partial<ThemeState>) {
  const store = loadProgress();
  store[id] = { ...store[id], ...patch };
  try { localStorage.setItem(KEY, JSON.stringify(store)); } catch (_) { /* negeer */ }
  if (backendSync) backendSync(id, store[id]);
  return store[id];
}

// Voegt server-voortgang samen met lokale (hoogste doneModules; server-score wint
// als die er is, anders lokaal), schrijft het resultaat lokaal weg en geeft het terug.
export function mergeProgress(server: Partial<Record<ThemeId, ThemeState>>): Store {
  const local = loadProgress();
  const merged: Store = { ...local };
  (Object.keys(merged) as ThemeId[]).forEach((id) => {
    const s = server[id];
    if (!s) return;
    merged[id] = {
      sa: s.sa ?? local[id].sa,
      toets: s.toets ?? local[id].toets,
      doneModules: Math.max(s.doneModules || 0, local[id].doneModules || 0),
    };
  });
  try { localStorage.setItem(KEY, JSON.stringify(merged)); } catch (_) { /* negeer */ }
  return merged;
}

export function themeProgressPct(id: ThemeId): number {
  const st = loadProgress()[id];
  const total = themeById(id).mods;
  return total ? Math.round((st.doneModules / total) * 100) : 0;
}

// ---- Helpers ----
export const fmt = (n: number | null): string => (n == null ? '—' : n.toFixed(1).replace('.', ','));

export const saScore = (answers: (number | null)[]): number => {
  const vals = answers.filter((a): a is number => a != null);
  if (!vals.length) return 0;
  const avg = vals.reduce((p, c) => p + c, 0) / vals.length; // 1..5
  return Math.round((avg / 5) * 10 * 10) / 10; // /10, 1 decimaal
};

export const toetsScore = (answers: (number | null)[], qs: ToetsVraag[]): { score: number; correct: number } => {
  const correct = qs.reduce((p, q, i) => p + (answers[i] === q.correct ? 1 : 0), 0);
  return { score: Math.round((correct / qs.length) * 10 * 10) / 10, correct };
};

export const PASS = 5.5;

// ---- Zelfscan met categorieën (voor het spinnenweb) + twee varianten ----
export const SA: Record<ThemeId, SaSet> = { ai: aiSa, dg: dgSa, mw: mwSa, dv: dvSa, sm: smSa };

export type SaVariant = 'quick' | 'full';
export const SA_VARIANTS: { id: SaVariant; label: string; time: string; hint: string }[] = [
  { id: 'quick', label: 'Snelle scan', time: '± 1 minuut', hint: 'Een korte inschatting over alle onderdelen' },
  { id: 'full', label: 'Volledige scan', time: '± 5 minuten', hint: 'Een uitgebreid beeld met een score per onderdeel' },
];

export function saItemsFor(themeId: ThemeId, variant: SaVariant): SaItem[] {
  const all = SA[themeId].items;
  return variant === 'quick' ? all.filter((i) => i.quick) : all;
}

// Fisher-Yates: geeft een geschudde KOPIE terug (origineel blijft ongemoeid).
export function shuffle<T>(arr: T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Toets klaarzetten: vragen in willekeurige volgorde én per vraag de opties geschud,
// met de juiste-antwoord-index correct meeverschoven. Vraag en (juiste) antwoord blijven bij elkaar.
export function prepareToets(themeId: ThemeId): ToetsVraag[] {
  return shuffle(TOETS_QUESTIONS[themeId]).map((q) => {
    const order = shuffle(q.opts.map((_, i) => i));
    return { q: q.q, opts: order.map((i) => q.opts[i]), correct: order.indexOf(q.correct) };
  });
}

// Zelfscan klaarzetten: stellingen in willekeurige volgorde (categorie reist mee).
export function prepareSa(themeId: ThemeId, variant: SaVariant): SaItem[] {
  return shuffle(saItemsFor(themeId, variant));
}

// Gemiddelde score (1-5) per categorie, in de vaste categorie-volgorde (radar-assen).
export function saCategoryScores(themeId: ThemeId, items: SaItem[], answers: (number | null)[]): { label: string; value: number }[] {
  return SA[themeId].categories.map((cat) => {
    const vals = items
      .map((it, i) => (it.category === cat ? answers[i] : null))
      .filter((v): v is number => v != null && v >= 1 && v <= 5);
    const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    return { label: cat, value: Math.round(avg * 10) / 10 };
  });
}
